import { useEffect, useMemo, useState } from "react";
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Switch, FormControlLabel, Alert, CircularProgress, Typography,
} from "@mui/material";
import { ACCENT } from "./accent";
import { PART_META, PART_FIELDS, buildPartPayload } from "./partSpecs";
import { createPart } from "./api/propulsion";

// Spec-driven dialog to add one catalog part. `type` selects the field spec;
// on success it calls onCreated(savedRow) so callers can refresh + auto-select.
const AddPartDialog = ({ open, type, onClose, onCreated }) => {
  const meta = PART_META[type] || {};
  const fields = useMemo(() => PART_FIELDS[type] || [], [type]);

  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setValues({});
      setError(null);
      setSaving(false);
    }
  }, [open, type]);

  const canSave = useMemo(
    () => fields.filter((f) => f.required).every((f) => String(values[f.key] || "").trim()),
    [fields, values]
  );

  const set = (field) => (e) =>
    setValues((v) => ({ ...v, [field.key]: field.type === "bool" ? e.target.checked : e.target.value }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await createPart(type, buildPartPayload(type, values));
      onCreated?.(saved);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Add {meta.label}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Brand and name are required. The rest is optional spec data — the more you provide, the closer the
          modeled result.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {fields.map((f) =>
            f.type === "bool" ? (
              <FormControlLabel
                key={f.key}
                sx={{ flex: "1 1 100%" }}
                control={<Switch checked={Boolean(values[f.key])} onChange={set(f)} size="small" />}
                label={<Typography variant="body2">{f.label}</Typography>}
              />
            ) : (
              <TextField
                key={f.key}
                sx={{ flex: f.key === "name" || f.key === "source" || f.key === "datasheet_url"
                  ? "1 1 100%" : "1 1 200px", minWidth: 180 }}
                size="small"
                label={f.label}
                required={f.required}
                select={f.type === "select"}
                type={f.type === "number" ? "number" : "text"}
                value={values[f.key] ?? ""}
                onChange={set(f)}
              >
                {f.type === "select" &&
                  f.options.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
              </TextField>
            )
          )}
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={save}
          disabled={saving || !canSave}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ backgroundColor: ACCENT, textTransform: "none", "&:hover": { backgroundColor: "#0058A5" } }}
        >
          Add {meta.label}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPartDialog;
